/**
 * WebSocket message handler functions for the Upgrade Medic update flow.
 * Each handler processes a specific message type from the server.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import { AgentLogEntry, JobRun, JobStage, LambdaFunction, SelectedVersions, Account, createInitialStages, JobStatus } from '../types';
import { UpgradeMedicDataService } from 'src/services/upgrade-medic/UpgradeMedicDataService';
import { PersistedEphemeralEntry } from '../hooks/useAgentPanel';
import { getWebSocketStage } from '../config';

/**
 * Strip pipeline name prefix from error messages.
 * Backend sends errors like "DaSPnLDetails: Build failed after 2 retry attempts".
 * Only strips if the prefix matches the actual pipeline name for the job.
 */
export const stripPipelinePrefix = (msg: string, pipelineName?: string): string => {
  if (!pipelineName || !msg) return msg;
  const prefix = `${pipelineName}: `;
  return msg.startsWith(prefix) ? msg.slice(prefix.length) : msg;
};

/** Shared deps passed to every handler. */
export interface MessageHandlerDeps {
  userAlias: string;
  selectedLambdas: LambdaFunction[];
  selectedVersions: SelectedVersions;
  accounts: Account[];
  currentConfigName: string;
  getSelectedPipelines: () => string[];
  addLog: (level: AgentLogEntry['level'], message: string, stage?: string, pipelineId?: string) => void;
  addPipelineLog: (pipelineId: string, level: AgentLogEntry['level'], message: string, stage?: string) => void;
  setIsAgentPanelVisible: (visible: boolean) => void;
  setJobStatus: (status: JobStatus) => void;
  setProgressMessage: (msg: string) => void;
  setStreamingText: React.Dispatch<React.SetStateAction<string>>;
  streamingTextRef: React.MutableRefObject<string>;
  setPersistedEphemeralEntries: React.Dispatch<React.SetStateAction<PersistedEphemeralEntry[]>>;
  setPipelinePersistedEphemeral: React.Dispatch<React.SetStateAction<Record<string, PersistedEphemeralEntry[]>>>;
  setPipelineStreamingText: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  pipelineStreamingTextRef: React.MutableRefObject<Record<string, string>>;
  setParallelPipelines: React.Dispatch<React.SetStateAction<string[]>>;
  setPipelineLogs: React.Dispatch<React.SetStateAction<Record<string, AgentLogEntry[]>>>;
  setActivePipelineTab: (tab: string) => void;
  setJobHistory: React.Dispatch<React.SetStateAction<JobRun[]>>;
  setCurrentJobId: (id: string | null) => void;
  currentJobIdRef: React.MutableRefObject<string | null>;
  isJobRunningRef: React.MutableRefObject<boolean>;
  jobHistory: JobRun[];
}

/** Classify raw text into a log level based on emoji/keyword heuristics. */
export const classifyLogLevel = (text: string): AgentLogEntry['level'] => {
  if (text.includes('✅') || text.includes('Success') || text.includes('success') || text.includes('Ready')) return 'phase';
  if (text.includes('❌') || /Failed(?!:\s*0)/.test(text) || text.includes('Error')) return 'error';
  if (text.includes('⚠️') || text.includes('Warning')) return 'warning';
  if (text.includes('🔧') || text.includes('📋') || text.includes('🔌') || text.includes('🤖')) return 'tool';
  return 'agent';
};

// ─── Connected ───

export const handleConnectedMessage = (message: any, ws: WebSocket, pipelineNames: string[], deps: MessageHandlerDeps): void => {
  const { addLog, addPipelineLog, setIsAgentPanelVisible, userAlias, selectedLambdas, selectedVersions, accounts } = deps;

  addLog('phase', `✅ ${message.message} (v${message.server_version})`);
  setIsAgentPanelVisible(true);

  const isManualOnlySelection = pipelineNames.every((name) => name === 'Manually created' || name === 'Manual Deployed Stacks');

  // Also log to the pipeline tab for manual flows
  if (isManualOnlySelection && pipelineNames.length > 0) {
    addPipelineLog(pipelineNames[0], 'phase', `✅ ${message.message} (v${message.server_version})`);
  }

  if (isManualOnlySelection) {
    sendManualUpdateCommand(ws, pipelineNames, deps);
  } else {
    sendPipelineUpdateCommands(ws, pipelineNames, deps);
  }
};

const sendManualUpdateCommand = (ws: WebSocket, pipelineNames: string[], deps: MessageHandlerDeps): void => {
  const { userAlias, selectedLambdas, selectedVersions, addLog } = deps;
  const stacks = new Map<string, LambdaFunction[]>();
  const standaloneLambdas: LambdaFunction[] = [];

  selectedLambdas.forEach((lambda) => {
    if (lambda.stackName && lambda.stackName !== 'Manually created (no stack)') {
      if (!stacks.has(lambda.stackName)) stacks.set(lambda.stackName, []);
      stacks.get(lambda.stackName)!.push(lambda);
    } else {
      standaloneLambdas.push(lambda);
    }
  });

  const manualUpdateCommand = {
    type: 'update_manual_resources',
    data: {
      userId: userAlias,
      stage: getWebSocketStage(),
      stacks: Array.from(stacks.entries()).map(([stackName, lambdas]) => ({
        stackName,
        region: lambdas[0].region,
        lambdas: lambdas.map((l) => ({ name: l.name, arn: l.arn, region: l.region, runtime: l.runtime }))
      })),
      lambdas: standaloneLambdas.map((l) => ({ name: l.name, arn: l.arn, region: l.region, runtime: l.runtime })),
      targetVersions: selectedVersions
    }
  };

  ws.send(JSON.stringify(manualUpdateCommand));
  addLog('phase', `🚀 Sent manual update for ${stacks.size} stack(s) and ${standaloneLambdas.length} lambda(s)`);

  // Transition to running immediately — backend may not send manual_resources_update_started
  deps.setJobStatus('running');
  deps.isJobRunningRef.current = true;

  // Also log to the pipeline tab so it appears in the active tab
  const manualTabId = pipelineNames.includes('Manual Deployed Stacks') ? 'Manual Deployed Stacks' : pipelineNames[0];
  if (manualTabId) {
    deps.addPipelineLog(manualTabId, 'phase', `🚀 Sent manual update for ${stacks.size} stack(s) and ${standaloneLambdas.length} lambda(s)`);
  }
};

const sendPipelineUpdateCommands = (ws: WebSocket, pipelineNames: string[], deps: MessageHandlerDeps): void => {
  const { userAlias, selectedLambdas, selectedVersions, accounts, addLog, addPipelineLog } = deps;
  const deployToAlpha = (deps as any).deployToAlpha;

  pipelineNames.forEach((pipelineName, index) => {
    const pipelineLambdas = selectedLambdas.filter((l) => {
      let belongsToPipeline = false;
      accounts.forEach((account) => {
        account.pipelines.forEach((p) => {
          if (p.name === pipelineName && p.lambdas.some((pl) => pl.id === l.id)) {
            belongsToPipeline = true;
          }
        });
      });
      return belongsToPipeline;
    });

    const startUpdateCommand = {
      type: 'start_update',
      data: {
        userId: userAlias,
        stage: getWebSocketStage(),
        pipelines: [pipelineName],
        updateType: 'lambda_runtime' as const,
        functions: pipelineLambdas.map((l) => l.name),
        targetVersions: selectedVersions,
        deployToAlpha: deployToAlpha
      }
    };

    setTimeout(() => {
      ws.send(JSON.stringify(startUpdateCommand));
      addPipelineLog(pipelineName, 'tool', '🚀 Sent update command for pipeline');
    }, index * 100);
  });

  addLog('phase', `🚀 Started ${pipelineNames.length} parallel job(s): ${pipelineNames.join(', ')}`);
};

// ─── Job Started ───

export const handleJobStartedMessage = (message: any, pipelineNames: string[], deps: MessageHandlerDeps): void => {
  const {
    userAlias,
    selectedLambdas,
    selectedVersions,
    currentConfigName,
    getSelectedPipelines,
    addLog,
    setJobStatus,
    setProgressMessage,
    setIsAgentPanelVisible,
    setParallelPipelines,
    setPipelineLogs,
    setActivePipelineTab,
    setJobHistory,
    setCurrentJobId,
    currentJobIdRef,
    isJobRunningRef
  } = deps;
  const deployToAlpha = (deps as any).deployToAlpha;

  setJobStatus('running');
  isJobRunningRef.current = true;
  setCurrentJobId(message.jobId);
  currentJobIdRef.current = message.jobId;
  setProgressMessage(message.message);
  setIsAgentPanelVisible(true);

  const selectedPipelineNames = getSelectedPipelines();
  setParallelPipelines((prev) => {
    const newPipelines = selectedPipelineNames.filter((name: string) => !prev.includes(name));
    return [...prev, ...newPipelines];
  });
  setPipelineLogs((prev) => {
    const updated = { ...prev };
    selectedPipelineNames.forEach((name: string) => {
      if (!updated[name]) updated[name] = [];
    });
    return updated;
  });

  const jobPipelineId = message.pipelineId || message.pipeline || (message.pipelines?.length === 1 ? message.pipelines[0] : undefined);
  const jobTotalResources = message.totalResources ?? message.functionCount;

  const newJob: JobRun = {
    jobId: message.jobId,
    pipelineId: jobPipelineId,
    status: 'running',
    triggeredBy: userAlias,
    startedAt: new Date(),
    pipelines: jobPipelineId ? [jobPipelineId] : getSelectedPipelines(),
    functions: selectedLambdas.map((l) => l.name),
    targetVersions: selectedVersions,
    resourceType: currentConfigName,
    progress: 0,
    progressMessage: message.message,
    currentStage: JobStage.INITIALIZING,
    stages: createInitialStages(!!deployToAlpha),
    artifacts: {},
    totalResources: jobTotalResources,
    logs: [],
    streamingText: ''
  };
  setJobHistory((prev) => {
    if (prev.some((j) => j.jobId === newJob.jobId)) return prev;
    return [...prev, newJob];
  });
  UpgradeMedicDataService.createJobRun(newJob).catch((err) => console.error('[Upgrade Medic] Failed to persist job to DynamoDB:', err));
  addLog('phase', `📋 Job started: ${message.jobId}`);
  addLog('info', message.message);
};

// ─── Progress ───

export const handleProgressMessage = (message: any, deps: MessageHandlerDeps): void => {
  if (message.progress === undefined || message.message === undefined) {
    console.log('[Upgrade Medic] Skipping progress with missing data:', message);
    return;
  }
  deps.setProgressMessage(`${message.progress}% - ${message.message}`);
  if (message.pipelineId) {
    deps.addPipelineLog(message.pipelineId, 'agent', `[${message.progress}%] ${message.message}`);
  } else {
    deps.addLog('agent', `[${message.progress}%] ${message.message}`);
  }
};

// ─── Job Breakdown ───

export const handleJobBreakdownMessage = (message: any, deps: MessageHandlerDeps): void => {
  const { setParallelPipelines, setPipelineLogs, addLog } = deps;
  const pipelineIds = message.pipelines.map((p: any) => p.pipelineId);

  setParallelPipelines((prev) => {
    const newPipelines = pipelineIds.filter((id: string) => !prev.includes(id));
    return [...prev, ...newPipelines];
  });
  setPipelineLogs((prev) => {
    const updated = { ...prev };
    pipelineIds.forEach((id: string) => {
      if (!updated[id]) updated[id] = [];
    });
    return updated;
  });
  addLog('phase', `📊 Job breakdown: ${pipelineIds.length} pipeline(s) queued - ${pipelineIds.join(', ')}`);
};

// ─── Agent Streaming Events ───

export const handleAgentTextMessage = (message: any, deps: MessageHandlerDeps): void => {
  const { setPipelineStreamingText, pipelineStreamingTextRef, setStreamingText, streamingTextRef } = deps;

  if (message.pipelineId) {
    setPipelineStreamingText((prev) => ({
      ...prev,
      [message.pipelineId]: (prev[message.pipelineId] || '') + message.text
    }));
    pipelineStreamingTextRef.current = {
      ...pipelineStreamingTextRef.current,
      [message.pipelineId]: (pipelineStreamingTextRef.current[message.pipelineId] || '') + message.text
    };
  } else {
    // Only append to global streaming when no pipeline-specific target
    setStreamingText((prev) => {
      const newText = prev + message.text;
      streamingTextRef.current = newText;
      return newText;
    });
  }
};

export const handleAgentToolStartMessage = (message: any, parallelPipelines: string[], deps: MessageHandlerDeps): void => {
  const {
    streamingTextRef,
    setStreamingText,
    setPersistedEphemeralEntries,
    setPipelinePersistedEphemeral,
    setPipelineStreamingText,
    pipelineStreamingTextRef,
    addLog,
    addPipelineLog
  } = deps;

  if (message.pipelineId) {
    const pipelineText = pipelineStreamingTextRef.current[message.pipelineId];
    if (pipelineText && pipelineText.trim().length > 0) {
      setPipelinePersistedEphemeral((prev) => ({
        ...prev,
        [message.pipelineId]: [
          ...(prev[message.pipelineId] || []),
          { id: Date.now(), timestamp: new Date(), text: pipelineText.trim(), isExpanded: false }
        ]
      }));
      pipelineStreamingTextRef.current = { ...pipelineStreamingTextRef.current, [message.pipelineId]: '' };
    }
    setPipelineStreamingText((prev) => ({ ...prev, [message.pipelineId]: '' }));
  } else {
    // Only persist/clear global streaming when no pipeline-specific target
    if (streamingTextRef.current && streamingTextRef.current.trim().length > 0) {
      const persistedEntry = {
        id: Date.now(),
        timestamp: new Date(),
        text: streamingTextRef.current.trim(),
        isExpanded: false
      };
      setPersistedEphemeralEntries((prev) => [...prev, persistedEntry]);
      streamingTextRef.current = '';
    }
    setStreamingText('');
  }

  if (message.pipelineId) {
    addPipelineLog(message.pipelineId, 'tool', `🔧 [Tool #${message.count}] ${message.tool}: ${message.display}`);
  } else {
    addLog('tool', `🔧 [Tool #${message.count}] ${message.tool}: ${message.display}`);
  }
};

// ─── Stage & Artifact Events ───

export const handleStageUpdateMessage = (message: any, deps: MessageHandlerDeps): void => {
  const { isJobRunningRef, setJobHistory, setProgressMessage, addLog, addPipelineLog, setCurrentJobId, currentJobIdRef, jobHistory } = deps;

  if (!message.stage || !message.jobId) {
    console.log('[Upgrade Medic] Skipping stage_update with missing data:', message);
    return;
  }
  if (!isJobRunningRef.current) {
    console.log('[Upgrade Medic] Skipping stage_update after job completed:', message.stage);
    return;
  }

  // For manual update flows the backend sends standard stage_update / job_completed
  // messages without a preceding job_started or manual_resources_update_started.
  // If no JobRun exists for this jobId yet, create one so the Job Executions table
  // shows the in-progress entry and subsequent updates can match by jobId.
  const jobExists = jobHistory.some((j) => j.jobId === message.jobId);
  if (!jobExists) {
    setCurrentJobId(message.jobId);
    currentJobIdRef.current = message.jobId;

    const { userAlias, selectedLambdas, selectedVersions, currentConfigName } = deps;
    // Use backend-provided pipelineId (stack name or Lambda ARN) with UI-friendly fallback
    const manualPipelineId = message.pipelineId || message.pipeline_id || 'Manual Deployed Stacks';

    // Prefer backend-provided data over frontend selection state (which may be stale)
    const msgFunctions: string[] = message.functions || message.function_names || [];
    const msgTotalResources: number | undefined =
      message.totalResources ?? message.total_resources ?? message.functionCount ?? message.function_count;

    // Build functions list: prefer backend data, fall back to frontend selection
    const functionNames = msgFunctions.length > 0 ? msgFunctions : selectedLambdas.map((l) => l.name);
    const totalResources = msgTotalResources ?? (functionNames.length > 0 ? functionNames.length : selectedLambdas.length);

    const stackToLambdasMap: Record<string, string[]> = {};
    selectedLambdas.forEach((lambda) => {
      if (lambda.stackName && lambda.stackName !== 'Manually created (no stack)') {
        if (!stackToLambdasMap[lambda.stackName]) stackToLambdasMap[lambda.stackName] = [];
        stackToLambdasMap[lambda.stackName].push(lambda.name);
      }
    });

    const newJob: JobRun = {
      jobId: message.jobId,
      pipelineId: manualPipelineId,
      status: 'running',
      triggeredBy: message.userId || message.user_id || userAlias,
      startedAt: new Date(),
      pipelines: [manualPipelineId],
      functions: functionNames,
      targetVersions: message.targetVersions || message.target_versions || selectedVersions,
      resourceType: message.resourceType || message.resource_type || currentConfigName,
      progress: 0,
      progressMessage: message.stageMessage || 'Manual update in progress...',
      currentStage: message.stage as JobStage,
      stages: createInitialStages(),
      artifacts: {
        stackNames: Array.from(new Set(selectedLambdas.map((l) => l.stackName).filter(Boolean))) as string[],
        stackToLambdas: Object.keys(stackToLambdasMap).length > 0 ? stackToLambdasMap : undefined
      },
      totalResources,
      logs: [],
      streamingText: ''
    };
    setJobHistory((prev) => {
      // Guard against duplicates — another handler may have already created this job
      if (prev.some((j) => j.jobId === message.jobId)) return prev;
      return [...prev, newJob];
    });
    addLog('phase', `🔄 Manual update started: ${message.jobId}`);
    addPipelineLog(manualPipelineId, 'phase', `🔄 Manual update started: ${message.jobId}`);
  }

  const stageProgress = message.overallProgress ?? message.progress;

  setJobHistory((prev) =>
    prev.map((job) => {
      if (job.jobId !== message.jobId) return job;

      // Enrich job with backend data if frontend created it with empty/stale values
      const enrichedFunctions =
        job.functions.length === 0 && (message.functions || message.function_names) ? message.functions || message.function_names : job.functions;
      const enrichedTotalResources =
        (job.totalResources === 0 || job.totalResources === undefined) &&
        (message.totalResources ?? message.total_resources ?? message.functionCount ?? message.function_count) != null
          ? message.totalResources ?? message.total_resources ?? message.functionCount ?? message.function_count
          : job.totalResources;
      const enrichedPipelineId =
        job.pipelineId === 'Manual Deployed Stacks' && message.pipelineId && message.pipelineId !== 'Manual Deployed Stacks'
          ? message.pipelineId
          : job.pipelineId;

      return {
        ...job,
        currentStage: message.stage as JobStage,
        progress: stageProgress ?? job.progress,
        progressMessage: message.stageMessage || job.progressMessage,
        pipelineId: enrichedPipelineId,
        functions: enrichedFunctions,
        totalResources: enrichedTotalResources,
        stages: {
          ...job.stages,
          [message.stage]: {
            status: message.stageStatus,
            startedAt: message.stageStartedAt,
            completedAt: message.stageCompletedAt,
            message: message.stageMessage
          }
        },
        artifacts: message.artifacts || job.artifacts
      };
    })
  );

  // Compute display percentage: use backend value if present, otherwise derive from stage index
  let displayPercent = stageProgress;
  if (displayPercent == null) {
    const stageOrder = [
      JobStage.INITIALIZING,
      JobStage.IMPACT_ANALYSIS,
      JobStage.CODE_CHANGES,
      JobStage.BUILD,
      JobStage.PIPELINE_TRIGGER,
      JobStage.CLEANUP
    ];
    const idx = stageOrder.indexOf(message.stage as JobStage);
    if (idx >= 0) {
      displayPercent = Math.round(((idx + 1) / stageOrder.length) * 100);
    } else {
      displayPercent = 0;
    }
  }

  setProgressMessage(`[${displayPercent}%] ${message.stage}: ${message.stageMessage || 'Processing...'}`);

  if (message.pipelineId) {
    addPipelineLog(message.pipelineId, 'phase', `Stage: ${message.stage} - ${message.stageMessage || 'Processing...'}`);
  } else {
    // For manual flows, also route to the pipeline tab so logs appear in the active tab
    const matchedJob = deps.jobHistory.find((j) => j.jobId === message.jobId);
    const fallbackPipelineId = matchedJob?.pipelineId;
    addLog('phase', `Stage: ${message.stage} - ${message.stageMessage || 'Processing...'}`);
    if (fallbackPipelineId) {
      addPipelineLog(fallbackPipelineId, 'phase', `Stage: ${message.stage} - ${message.stageMessage || 'Processing...'}`);
    }
  }

  UpgradeMedicDataService.updateJobStage(
    message.jobId,
    message.stage as JobStage,
    {
      status: message.stageStatus,
      startedAt: message.stageStartedAt,
      completedAt: message.stageCompletedAt,
      message: message.stageMessage
    },
    displayPercent,
    message.stageMessage || ''
  ).catch((err) => console.error('[Upgrade Medic] Failed to persist stage update:', err));
};

export const handleArtifactCreatedMessage = (message: any, deps: MessageHandlerDeps): void => {
  const { setJobHistory, addLog } = deps;

  setJobHistory((prev) =>
    prev.map((job) => (job.jobId === message.jobId ? { ...job, artifacts: { ...job.artifacts, [message.artifactType]: message.artifactUrl } } : job))
  );
  addLog('tool', `🔗 ${message.artifactType}: ${message.artifactTitle || message.artifactUrl}`);

  UpgradeMedicDataService.updateJobArtifact(
    message.jobId,
    message.artifactType as 'buildLink' | 'crLink' | 'pipelineLink',
    message.artifactUrl
  ).catch((err) => console.error('[Upgrade Medic] Failed to persist artifact:', err));
};

// ─── Log & Output ───

export const handleLogMessage = (message: any, deps: MessageHandlerDeps): void => {
  const { addLog, addPipelineLog, currentJobIdRef, setJobHistory } = deps;
  const logLevel = message.level || 'info';
  const logMessage = message.message || message.data || JSON.stringify(message);
  const logPipelineId = message.pipelineId;

  let logType: AgentLogEntry['level'] = 'agent';
  if (logMessage.includes('✅') || logMessage.includes('Success') || logMessage.includes('success')) {
    logType = 'phase';
  } else if (logLevel === 'error' || logMessage.includes('❌') || /Failed(?!:\s*0)/.test(logMessage)) {
    logType = 'error';
  } else if (logLevel === 'warning' || logMessage.includes('⚠️')) {
    logType = 'warning';
  }

  if (logPipelineId) {
    addPipelineLog(logPipelineId, logType, logMessage);
  } else {
    addLog(logType, logMessage);
  }

  if (logType === 'error') {
    const jobIdForError = message.jobId || currentJobIdRef.current;
    if (jobIdForError) {
      setJobHistory((prev) => prev.map((job) => (job.jobId === jobIdForError ? { ...job, progressMessage: logMessage } : job)));
    }
  }
};

// ─── Job Completed ───

export const handleJobCompletedMessage = (message: any, deps: MessageHandlerDeps): void => {
  const {
    setJobStatus,
    setProgressMessage,
    isJobRunningRef,
    setJobHistory,
    addLog,
    jobHistory,
    userAlias,
    selectedLambdas,
    selectedVersions,
    currentConfigName,
    setCurrentJobId,
    currentJobIdRef,
    addPipelineLog
  } = deps;

  const backendStatus = message.status || message.job_status || message.final_status || '';
  const isAlphaDeploymentFailure =
    backendStatus.toLowerCase() === 'completed_with_alpha_deployment_failure' ||
    backendStatus.toLowerCase() === 'completed_with_alpha_deployment_failures';

  const completedStatus: JobRun['status'] = isAlphaDeploymentFailure
    ? 'completed_with_alpha_deployment_failure'
    : message.summary.failed > 0
    ? 'failed'
    : 'completed';
  setJobStatus(completedStatus === 'completed_with_alpha_deployment_failure' ? 'completed' : completedStatus);
  setProgressMessage(`Completed: ${message.summary.success}/${message.summary.total} succeeded`);
  isJobRunningRef.current = false;

  // Extract error message from failed results for display
  const failedResults = (message.results || []).filter((r: any) => !r.success);
  const completionErrorMsg = failedResults.length > 0 ? failedResults.map((r: any) => `${r.pipeline}: ${r.error || 'Failed'}`).join('; ') : undefined;

  // For manual flows: if no JobRun exists yet (no stage_update arrived first), create one
  // with completed status so it appears in the Job Executions table.
  const jobExists = jobHistory.some((j) => j.jobId === message.jobId);
  if (!jobExists && message.jobId) {
    setCurrentJobId(message.jobId);
    currentJobIdRef.current = message.jobId;

    const manualPipelineId = message.pipelineId || message.pipeline_id || 'Manual Deployed Stacks';

    // Prefer backend-provided data over frontend selection state
    const msgFunctions: string[] = message.functions || message.function_names || [];
    const functionNames = msgFunctions.length > 0 ? msgFunctions : selectedLambdas.map((l) => l.name);

    const stackToLambdasMap: Record<string, string[]> = {};
    selectedLambdas.forEach((lambda) => {
      if (lambda.stackName && lambda.stackName !== 'Manually created (no stack)') {
        if (!stackToLambdasMap[lambda.stackName]) stackToLambdasMap[lambda.stackName] = [];
        stackToLambdasMap[lambda.stackName].push(lambda.name);
      }
    });

    const completedJob: JobRun = {
      jobId: message.jobId,
      pipelineId: manualPipelineId,
      status: completedStatus,
      triggeredBy: message.userId || message.user_id || userAlias,
      startedAt: new Date(),
      completedAt: new Date(),
      pipelines: [manualPipelineId],
      functions: functionNames,
      targetVersions: message.targetVersions || message.target_versions || selectedVersions,
      resourceType: message.resourceType || message.resource_type || currentConfigName,
      progress: 100,
      progressMessage: `Completed: ${message.summary.success}/${message.summary.total} succeeded`,
      currentStage: completedStatus === 'completed' ? JobStage.COMPLETED : JobStage.FAILED,
      stages: {},
      artifacts: {
        stackNames: Array.from(new Set(selectedLambdas.map((l) => l.stackName).filter(Boolean))) as string[],
        stackToLambdas: Object.keys(stackToLambdasMap).length > 0 ? stackToLambdasMap : undefined
      },
      totalResources: message.summary.total || functionNames.length || selectedLambdas.length,
      logs: [],
      streamingText: '',
      result: { success: message.summary.success, failed: message.summary.failed, total: message.summary.total },
      ...(completionErrorMsg ? { error: { code: 'PARTIAL_FAILURE', message: completionErrorMsg, stage: JobStage.COMPLETED, retryable: true } } : {})
    };
    setJobHistory((prev) => {
      if (prev.some((j) => j.jobId === message.jobId)) return prev;
      return [...prev, completedJob];
    });
    addPipelineLog(manualPipelineId, 'phase', `✅ Job completed: ${message.summary.success}/${message.summary.total} succeeded`);
  }

  setJobHistory((prev) => {
    return prev.map((job) => {
      if (job.jobId === message.jobId) {
        // Don't overwrite cancelled status — cancel takes priority over backend completion
        if (job.status === 'cancelled') return job;

        const crLinks: string[] =
          message.cr?.urls ||
          message.artifacts?.crLinks ||
          (message.crLink ? [message.crLink] : []) ||
          (message.artifacts?.crLink ? [message.artifacts.crLink] : []) ||
          job.artifacts?.crLinks ||
          (job.artifacts?.crLink ? [job.artifacts.crLink] : []);

        const completedArtifacts = {
          ...job.artifacts,
          crLink: crLinks.length > 0 ? crLinks[0] : job.artifacts?.crLink,
          crLinks: crLinks.length > 0 ? crLinks : job.artifacts?.crLinks,
          buildLink: message.buildLink || message.artifacts?.buildLink || job.artifacts?.buildLink,
          manualActions: message.manualActions || message.artifacts?.manualActions || job.artifacts?.manualActions,
          pipelineLink: message.pipelineLink || message.artifacts?.pipelineLink || job.artifacts?.pipelineLink
        };

        const completedJob: JobRun = {
          ...job,
          status: completedStatus,
          completedAt: new Date(),
          artifacts: completedArtifacts,
          result: {
            success: message.summary.success,
            failed: message.summary.failed,
            total: message.summary.total
          },
          ...(completionErrorMsg
            ? {
                error: {
                  code: 'PARTIAL_FAILURE',
                  message: completionErrorMsg,
                  stage: job.currentStage || JobStage.INITIALIZING,
                  retryable: true
                }
              }
            : {})
        };

        UpgradeMedicDataService.completeJobRun(
          job.jobId,
          completedStatus,
          job.stages,
          completedArtifacts,
          {
            success: message.summary.success,
            failed: message.summary.failed,
            total: message.summary.total
          },
          completionErrorMsg
            ? {
                code: 'PARTIAL_FAILURE',
                message: completionErrorMsg,
                stage: job.currentStage || JobStage.INITIALIZING,
                retryable: true
              }
            : undefined
        ).catch((err) => console.error('[Upgrade Medic] Failed to persist job completion to DynamoDB:', err));

        return completedJob;
      }
      return job;
    });
  });

  if (message.summary.total === 1) {
    const result = message.results?.[0];
    if (result?.success) {
      addLog('phase', `✅ ${result.pipeline}: Success`);
    } else {
      addLog('phase', '📋 Job completed');
    }
  } else {
    if (message.summary.failed === 0) {
      addLog('phase', `✅ All ${message.summary.total} pipeline(s) succeeded`);
    } else if (message.summary.success === 0) {
      addLog('error', `❌ All ${message.summary.total} pipeline(s) failed`);
    } else {
      addLog('warning', `⚠️ ${message.summary.success}/${message.summary.total} succeeded`);
    }
    message.results?.forEach((r: any) => {
      if (r.success) {
        addLog('info', `   ✓ ${r.pipeline}`);
      } else {
        addLog('error', `   ✗ ${r.pipeline}: ${r.error || 'Failed'}`);
      }
    });
  }
};

// ─── Job Failed ───

export const handleJobFailedMessage = (message: any, deps: MessageHandlerDeps): void => {
  const {
    setJobStatus,
    isJobRunningRef,
    setProgressMessage,
    currentJobIdRef,
    setJobHistory,
    jobHistory,
    addLog,
    userAlias,
    selectedLambdas,
    selectedVersions,
    currentConfigName,
    setCurrentJobId,
    addPipelineLog
  } = deps;

  setJobStatus('failed');
  isJobRunningRef.current = false;
  const pipelineName = message.pipelineId || message.pipeline_id || message.pipeline || '';
  const errorMsg = stripPipelinePrefix(message.error || message.message || 'Unknown error', pipelineName);
  setProgressMessage(`Failed: ${errorMsg}`);

  const failedJobId = message.jobId || currentJobIdRef.current;

  // For manual flows: if no JobRun exists yet, create one with failed status
  const jobExists = failedJobId ? jobHistory.some((j) => j.jobId === failedJobId) : false;
  if (!jobExists && failedJobId) {
    setCurrentJobId(failedJobId);
    currentJobIdRef.current = failedJobId;

    const manualPipelineId = message.pipelineId || message.pipeline_id || 'Manual Deployed Stacks';

    // Prefer backend-provided data over frontend selection state
    const msgFunctions: string[] = message.functions || message.function_names || [];
    const functionNames = msgFunctions.length > 0 ? msgFunctions : selectedLambdas.map((l) => l.name);

    const failedJob: JobRun = {
      jobId: failedJobId,
      pipelineId: manualPipelineId,
      status: 'failed',
      triggeredBy: message.userId || message.user_id || userAlias,
      startedAt: new Date(),
      completedAt: new Date(),
      pipelines: [manualPipelineId],
      functions: functionNames,
      targetVersions: message.targetVersions || message.target_versions || selectedVersions,
      resourceType: message.resourceType || message.resource_type || currentConfigName,
      progress: 0,
      progressMessage: errorMsg,
      currentStage: JobStage.FAILED,
      stages: {},
      artifacts: {},
      totalResources: (message.totalResources ?? message.total_resources ?? functionNames.length) || selectedLambdas.length,
      logs: [],
      streamingText: '',
      error: { code: message.error_code || 'JOB_FAILED', message: errorMsg, stage: JobStage.INITIALIZING, retryable: message.retryable ?? false }
    };
    setJobHistory((prev) => {
      if (prev.some((j) => j.jobId === failedJobId)) return prev;
      return [...prev, failedJob];
    });
    addPipelineLog(manualPipelineId, 'error', `❌ Job failed: ${errorMsg}`);
  }

  setJobHistory((prev) =>
    prev.map((job) =>
      job.jobId === failedJobId && job.status !== 'cancelled'
        ? {
            ...job,
            status: 'failed' as const,
            completedAt: new Date(),
            progressMessage: errorMsg,
            error: {
              code: message.error_code || 'JOB_FAILED',
              message: errorMsg,
              stage: message.failedStage || job.currentStage || JobStage.INITIALIZING,
              retryable: message.retryable ?? false
            }
          }
        : job
    )
  );

  if (failedJobId) {
    const failedJob = jobHistory.find((j) => j.jobId === failedJobId);
    UpgradeMedicDataService.completeJobRun(failedJobId, 'failed', failedJob?.stages || {}, failedJob?.artifacts || {}, undefined, {
      code: message.error_code || 'JOB_FAILED',
      message: errorMsg,
      stage: message.failedStage || failedJob?.currentStage || JobStage.INITIALIZING,
      retryable: message.retryable ?? false
    })
      .then(async () => {
        const updatedJob = await UpgradeMedicDataService.getJobRunById(failedJobId);
        if (updatedJob) {
          setJobHistory((prev) =>
            prev.map((job) =>
              job.jobId === failedJobId
                ? {
                    ...job,
                    error: updatedJob.error,
                    progressMessage: updatedJob.error?.message || updatedJob.progressMessage || errorMsg
                  }
                : job
            )
          );
        }
      })
      .catch((err) => console.error('[Upgrade Medic] Failed to persist job failure to DynamoDB:', err));
  }

  addLog('error', `❌ Job failed: ${errorMsg}`);
};

// ─── Job Cancelled ───

export const handleJobCancelledMessage = (message: any, deps: MessageHandlerDeps): void => {
  const { setJobStatus, isJobRunningRef, setProgressMessage, setJobHistory, addLog } = deps;
  setJobStatus('idle');
  isJobRunningRef.current = false;
  setProgressMessage('Job cancelled');
  setJobHistory((prev) => prev.map((job) => (job.jobId === message.jobId ? { ...job, status: 'cancelled' as const, completedAt: new Date() } : job)));
  addLog('warning', `⚠️ Job cancelled: ${message.jobId}`);
};

// ─── Manual Resources Update ───

export const handleManualResourcesStartedMessage = (message: any, pipelineNames: string[], deps: MessageHandlerDeps): void => {
  const {
    userAlias,
    selectedLambdas,
    selectedVersions,
    currentConfigName,
    setJobStatus,
    setProgressMessage,
    isJobRunningRef,
    setCurrentJobId,
    currentJobIdRef,
    setJobHistory,
    addLog,
    addPipelineLog
  } = deps;

  setJobStatus('running');
  isJobRunningRef.current = true;
  setCurrentJobId(message.jobId);
  currentJobIdRef.current = message.jobId;
  setProgressMessage(`Updating ${message.totalLambdas || 0} lambda(s) and ${message.totalStacks || 0} stack(s)...`);

  const stackToLambdasMap: Record<string, string[]> = {};
  selectedLambdas.forEach((lambda) => {
    if (lambda.stackName && lambda.stackName !== 'Manually created (no stack)') {
      if (!stackToLambdasMap[lambda.stackName]) stackToLambdasMap[lambda.stackName] = [];
      stackToLambdasMap[lambda.stackName].push(lambda.name);
    }
  });

  const manualPipelineId = pipelineNames.includes('Manual Deployed Stacks') ? 'Manual Deployed Stacks' : 'Manually created';

  const manualJob: JobRun = {
    jobId: message.jobId,
    pipelineId: manualPipelineId,
    status: 'running',
    triggeredBy: userAlias,
    startedAt: new Date(),
    pipelines: pipelineNames,
    functions: selectedLambdas.map((l) => l.name),
    targetVersions: selectedVersions,
    resourceType: currentConfigName,
    progress: 0,
    progressMessage: `Updating ${message.totalLambdas || 0} lambda(s) and ${message.totalStacks || 0} stack(s)...`,
    currentStage: JobStage.INITIALIZING,
    stages: createInitialStages(),
    artifacts: {
      stackNames: Array.from(new Set(selectedLambdas.map((l) => l.stackName).filter(Boolean))) as string[],
      stackToLambdas: stackToLambdasMap
    },
    totalResources: message.totalLambdas || selectedLambdas.length,
    logs: [],
    streamingText: ''
  };

  setJobHistory((prev) => {
    if (prev.some((j) => j.jobId === manualJob.jobId)) return prev;
    return [...prev, manualJob];
  });
  UpgradeMedicDataService.createJobRun(manualJob).catch((err) => console.error('[Upgrade Medic] Failed to persist manual job to DynamoDB:', err));
  const logMsg = `🔄 Manual update started: ${message.totalLambdas || 0} lambda(s), ${message.totalStacks || 0} stack(s)`;
  addLog('phase', logMsg);
  addPipelineLog(manualPipelineId, 'phase', logMsg);
};

export const handleManualResourcesCompletedMessage = (message: any, deps: MessageHandlerDeps): void => {
  const {
    setJobStatus,
    setProgressMessage,
    isJobRunningRef,
    setJobHistory,
    addLog,
    addPipelineLog,
    jobHistory,
    setCurrentJobId,
    currentJobIdRef,
    userAlias,
    selectedLambdas,
    selectedVersions,
    currentConfigName
  } = deps;

  const manualSummary = message.summary || {};
  const totalUpdated = (manualSummary.stacksUpdated || 0) + (manualSummary.lambdasUpdated || 0);
  const totalFailed = (manualSummary.stacksFailed || 0) + (manualSummary.lambdasFailed || 0);
  const manualCompletedStatus: JobRun['status'] = totalFailed === 0 ? 'completed' : 'failed';

  // Derive the pipeline tab ID from the job so logs appear in the correct tab
  const matchingJob = jobHistory.find((j) => j.jobId === message.jobId);
  const manualPipelineId = matchingJob?.pipelineId || 'Manual Deployed Stacks';

  // If the job doesn't exist yet (backend skipped manual_resources_update_started), create it
  if (!matchingJob && message.jobId) {
    setCurrentJobId(message.jobId);
    currentJobIdRef.current = message.jobId;

    const completedJob: JobRun = {
      jobId: message.jobId,
      pipelineId: manualPipelineId,
      status: manualCompletedStatus,
      triggeredBy: userAlias,
      startedAt: new Date(),
      completedAt: new Date(),
      pipelines: [manualPipelineId],
      functions: selectedLambdas.map((l) => l.name),
      targetVersions: selectedVersions,
      resourceType: currentConfigName,
      progress: 100,
      progressMessage:
        totalFailed === 0
          ? `✅ Manual update completed: ${totalUpdated} resource(s) updated`
          : `⚠️ Manual update completed with errors: ${totalUpdated}/${totalUpdated + totalFailed} succeeded`,
      currentStage: totalFailed === 0 ? JobStage.COMPLETED : JobStage.FAILED,
      stages: {},
      artifacts: {},
      totalResources: totalUpdated + totalFailed,
      logs: [],
      streamingText: '',
      result: { success: totalUpdated, failed: totalFailed, total: totalUpdated + totalFailed }
    };
    setJobHistory((prev) => {
      if (prev.some((j) => j.jobId === completedJob.jobId)) return prev;
      return [...prev, completedJob];
    });
    UpgradeMedicDataService.createJobRun(completedJob).catch((err) =>
      console.error('[Upgrade Medic] Failed to persist manual job to DynamoDB:', err)
    );
  }

  const logToBoth = (level: AgentLogEntry['level'], msg: string) => {
    addLog(level, msg);
    addPipelineLog(manualPipelineId, level, msg);
  };

  if (totalFailed === 0) {
    setJobStatus('completed');
    setProgressMessage(`✅ Manual update completed: ${totalUpdated} resource(s) updated`);
    logToBoth('phase', '✅ Manual resources update completed successfully');
    logToBoth('phase', `   📦 Stacks: ${manualSummary.stacksUpdated || 0} updated, ${manualSummary.stacksFailed || 0} failed`);
    logToBoth('phase', `   λ Lambdas: ${manualSummary.lambdasUpdated || 0} updated, ${manualSummary.lambdasFailed || 0} failed`);
  } else {
    setJobStatus('failed');
    setProgressMessage(`⚠️ Manual update completed with errors: ${totalUpdated}/${totalUpdated + totalFailed} succeeded`);
    logToBoth('warning', '⚠️ Manual resources update completed with errors');
    logToBoth('warning', `   📦 Stacks: ${manualSummary.stacksUpdated || 0} updated, ${manualSummary.stacksFailed || 0} failed`);
    logToBoth('warning', `   λ Lambdas: ${manualSummary.lambdasUpdated || 0} updated, ${manualSummary.lambdasFailed || 0} failed`);
  }

  if (message.lambdaResults && message.lambdaResults.length > 0) {
    message.lambdaResults.forEach((result: any) => {
      if (result.success) {
        logToBoth('info', `   ✓ ${result.name}: ${result.fromRuntime} → ${result.toRuntime}`);
      } else {
        logToBoth('error', `   ✗ ${result.name}: ${result.error || 'Failed'}`);
      }
    });
  }

  if (message.stackResults && message.stackResults.length > 0) {
    message.stackResults.forEach((result: any) => {
      if (result.success) {
        logToBoth('info', `   ✓ Stack ${result.stackName}: Updated`);
      } else {
        logToBoth('error', `   ✗ Stack ${result.stackName}: ${result.error || 'Failed'}`);
      }
    });
  }

  // Only update existing job — if we just created it above, it already has the correct status
  if (matchingJob) {
    setJobHistory((prev) =>
      prev.map((job) =>
        job.jobId === message.jobId
          ? {
              ...job,
              status: manualCompletedStatus,
              completedAt: new Date(),
              result: { success: totalUpdated, failed: totalFailed, total: totalUpdated + totalFailed }
            }
          : job
      )
    );
  }

  UpgradeMedicDataService.completeJobRun(
    message.jobId,
    manualCompletedStatus,
    {},
    {},
    { success: totalUpdated, failed: totalFailed, total: totalUpdated + totalFailed }
  ).catch((err) => console.error('[Upgrade Medic] Failed to persist manual job completion:', err));

  isJobRunningRef.current = false;
};

// ─── Error & Default ───

export const handleErrorMessage = (message: any, deps: MessageHandlerDeps): void => {
  const { addLog, setJobStatus, setProgressMessage, isJobRunningRef } = deps;
  const errorCode = message.code;

  switch (errorCode) {
    case 'NO_SELECTION':
      addLog('error', '❌ No resources selected. Please select pipelines or functions to update.');
      setProgressMessage('No selection');
      break;
    case 'MIXED_SELECTION':
      addLog('error', '❌ Mixed selection types not allowed. Please select only pipelines OR stacks OR standalone lambdas.');
      setProgressMessage('Mixed selection not allowed');
      break;
    case 'TOO_MANY_RESOURCES':
      addLog('error', '❌ Too many resources selected. Maximum 3 pipelines per request allowed.');
      setProgressMessage('Max 3 pipelines per request');
      break;
    case 'JOB_LIMIT_EXCEEDED':
      addLog('error', '❌ Job limit exceeded. You already have the maximum number of concurrent jobs running.');
      setProgressMessage('Job limit exceeded');
      break;
    case 'RESOURCE_CONFLICT': {
      const conflicts = message.conflicts || [];
      const conflictPipelines = conflicts.map((c: any) => c.resourceId).join(', ');
      addLog('error', `❌ Resource conflict: ${conflictPipelines} already have active updates running.`);
      setProgressMessage(`Conflict: ${conflictPipelines}`);
      break;
    }
    default:
      addLog('error', `❌ Error: ${message.error}`);
  }

  setJobStatus('failed');
  isJobRunningRef.current = false;
};

export const handleDefaultMessage = (message: any, deps: MessageHandlerDeps): void => {
  const content = message.message || message.data || message.text || JSON.stringify(message, null, 2);
  deps.addLog(classifyLogLevel(content), content);
};

// ─── WebSocket onclose handler for main update flow ───

export const handleMainWsClose = (deps: MessageHandlerDeps): void => {
  const { isJobRunningRef, setJobStatus, setStreamingText, setJobHistory, setProgressMessage, addLog } = deps;

  if (!isJobRunningRef.current) return;

  isJobRunningRef.current = false;
  setJobStatus('failed');
  setStreamingText('');

  setJobHistory((prev) => {
    const failedJobs: string[] = [];

    const updatedJobs = prev.map((job) => {
      if (job.status === 'running' || job.status === 'connecting') {
        const existingError =
          job.progressMessage &&
          (job.progressMessage.includes('❌') || job.progressMessage.includes('Failed') || job.progressMessage.includes('Error'));

        const errorMsg = existingError ? job.progressMessage : 'Server disconnected unexpectedly';
        failedJobs.push(job.jobId);

        UpgradeMedicDataService.completeJobRun(job.jobId, 'failed', job.stages, job.artifacts, undefined, {
          code: 'DISCONNECTED',
          message: errorMsg,
          stage: job.currentStage || JobStage.INITIALIZING,
          retryable: true
        })
          .then(async () => {
            const updatedJob = await UpgradeMedicDataService.getJobRunById(job.jobId);
            if (updatedJob) {
              setJobHistory((prevJobs) =>
                prevJobs.map((j) =>
                  j.jobId === job.jobId
                    ? {
                        ...j,
                        error: updatedJob.error,
                        progressMessage: updatedJob.error?.message || updatedJob.progressMessage || errorMsg
                      }
                    : j
                )
              );
            }
          })
          .catch((err) => console.error(`[Upgrade Medic] Failed to persist job ${job.jobId} failure to DynamoDB:`, err));

        return {
          ...job,
          status: 'failed' as const,
          completedAt: new Date(),
          progressMessage: errorMsg,
          error: {
            code: 'DISCONNECTED',
            message: errorMsg,
            stage: job.currentStage || JobStage.INITIALIZING,
            retryable: true
          }
        };
      }
      return job;
    });

    if (failedJobs.length > 0) {
      setProgressMessage(`Failed: Server disconnected - ${failedJobs.length} job(s) affected`);
      addLog('error', `❌ Server disconnected unexpectedly - ${failedJobs.length} job(s) marked as failed: ${failedJobs.join(', ')}`);
    }

    return updatedJobs;
  });
};
